// ===========================================
// Payment Plans Tab
// ===========================================
// Manage subscription payment plans

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  InputAdornment,
  Typography,
  Button,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Star as DefaultIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  StarBorder as SetDefaultIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import DataTable, { Column } from '../../common/DataTable';
import FormModal from '../../common/FormModal';
import ConfirmDialog from '../../common/ConfirmDialog';
import PaymentOptionsModal from './PaymentOptionsModal';
import {
  useAdminPaymentPlans,
  useCreatePaymentPlan,
  useUpdatePaymentPlan,
  useDeletePaymentPlan,
  useSetDefaultPaymentPlan,
} from '../../../hooks/usePaymentPlans';
import {
  PaymentPlan,
  PaymentPlanType,
  PaymentInterval,
} from '../../../api/paymentPlans.api';

// ===========================================
// TYPES & OPTIONS
// ===========================================

const paymentPlanTypeOptions: { value: PaymentPlanType; label: string }[] = [
  { value: 'TERM', label: 'Term (Full Payment)' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'FORTNIGHTLY', label: 'Fortnightly' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'CUSTOM', label: 'Custom' },
];

const paymentIntervalOptions: { value: PaymentInterval; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'FORTNIGHTLY', label: 'Fortnightly' },
  { value: 'MONTHLY', label: 'Monthly' },
];

const typeColorMap: Record<PaymentPlanType, string> = {
  TERM: '#4580E4',
  MONTHLY: '#96DAC9',
  FORTNIGHTLY: '#FFCE00',
  WEEKLY: '#FFAE9E',
  CUSTOM: '#9DA5AF',
};

// ===========================================
// COMPONENT
// ===========================================

export default function PaymentPlansTab() {
  const { data, isLoading, error } = useAdminPaymentPlans();
  const createMutation = useCreatePaymentPlan();
  const updateMutation = useUpdatePaymentPlan();
  const deleteMutation = useDeletePaymentPlan();
  const setDefaultMutation = useSetDefaultPaymentPlan();

  const paymentPlans = data?.paymentPlans ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PaymentPlan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'MONTHLY' as PaymentPlanType,
    description: '',
    installments: '',
    interval: 'MONTHLY' as PaymentInterval,
    discountPercent: '',
    surchargePercent: '',
    isActive: true,
    isDefault: false,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<PaymentPlan | null>(null);

  // Payment options modal state
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);

  // Filter payment plans
  const filteredPlans = useMemo(() => {
    if (hideInactive) {
      return paymentPlans.filter((p) => p.isActive);
    }
    return paymentPlans;
  }, [paymentPlans, hideInactive]);

  // Helper: Can only delete inactive plans with no subscriptions
  const canDeletePlan = (plan: PaymentPlan) =>
    !plan.isActive && (!plan._count || plan._count.subscriptions === 0);

  // Handlers (defined before columns since columns reference them)
  const handleEdit = (plan: PaymentPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      type: plan.type,
      description: plan.description || '',
      installments: plan.installments?.toString() || '',
      interval: plan.interval || 'MONTHLY',
      discountPercent: plan.discountPercent ? parseFloat(plan.discountPercent).toString() : '',
      surchargePercent: plan.surchargePercent ? parseFloat(plan.surchargePercent).toString() : '',
      isActive: plan.isActive,
      isDefault: plan.isDefault,
    });
    setModalOpen(true);
  };

  const handleDelete = (plan: PaymentPlan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const handleSetDefault = async (plan: PaymentPlan) => {
    if (!plan.isDefault) {
      await setDefaultMutation.mutateAsync(plan.id);
    }
  };

  // Table columns
  const columns: Column<PaymentPlan>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 180,
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">{String(value)}</Typography>
          {row.isDefault && (
            <Chip
              icon={<DefaultIcon sx={{ fontSize: 14 }} />}
              label="Default"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20 }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 120,
      format: (value) => {
        const typeValue = value as PaymentPlanType;
        const typeInfo = paymentPlanTypeOptions.find((t) => t.value === typeValue);
        return (
          <Chip
            label={typeInfo?.label.split(' ')[0] ?? String(value)}
            size="small"
            sx={{
              bgcolor: typeColorMap[typeValue] || '#ccc',
              color: typeValue === 'FORTNIGHTLY' ? '#333' : '#fff',
            }}
          />
        );
      },
    },
    {
      id: 'installments',
      label: 'Installments',
      minWidth: 100,
      align: 'center',
      format: (value, row) => {
        if (row.type === 'TERM') return '-';
        if (!value) return '-';
        const intervalLabel = paymentIntervalOptions.find(
          (i) => i.value === row.interval
        )?.label || row.interval;
        return `${value} x ${intervalLabel}`;
      },
    },
    {
      id: 'discountPercent',
      label: 'Discount',
      minWidth: 80,
      align: 'right',
      format: (value) => {
        if (!value || parseFloat(String(value)) === 0) return '-';
        return (
          <Chip
            label={`-${parseFloat(String(value))}%`}
            size="small"
            color="success"
            variant="outlined"
          />
        );
      },
    },
    {
      id: 'surchargePercent',
      label: 'Surcharge',
      minWidth: 80,
      align: 'right',
      format: (value) => {
        if (!value || parseFloat(String(value)) === 0) return '-';
        return (
          <Chip
            label={`+${parseFloat(String(value))}%`}
            size="small"
            color="warning"
            variant="outlined"
          />
        );
      },
    },
    {
      id: '_count',
      label: 'Subscriptions',
      minWidth: 100,
      align: 'center',
      format: (value) => {
        const count = (value as { subscriptions: number } | undefined)?.subscriptions ?? 0;
        return count > 0 ? (
          <Chip label={count} size="small" variant="outlined" />
        ) : (
          '-'
        );
      },
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 80,
      align: 'center',
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 140,
      align: 'right',
      format: (_, row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {!row.isDefault && row.isActive && (
            <Tooltip title="Set as Default">
              <IconButton size="small" onClick={() => handleSetDefault(row)}>
                <SetDefaultIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDeletePlan(row) ? 'Delete' : 'Set Inactive first'}>
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(row)}
                disabled={!canDeletePlan(row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      type: 'MONTHLY',
      description: '',
      installments: '',
      interval: 'MONTHLY',
      discountPercent: '',
      surchargePercent: '',
      isActive: true,
      isDefault: false,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        type: formData.type,
        description: formData.description || undefined,
        installments: formData.installments ? parseInt(formData.installments, 10) : undefined,
        interval: formData.type !== 'TERM' ? formData.interval : undefined,
        discountPercent: formData.discountPercent ? parseFloat(formData.discountPercent) : undefined,
        surchargePercent: formData.surchargePercent ? parseFloat(formData.surchargePercent) : undefined,
        isActive: formData.isActive,
        isDefault: formData.isDefault,
      };

      if (editingPlan) {
        await updateMutation.mutateAsync({ id: editingPlan.id, data });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (planToDelete) {
      try {
        await deleteMutation.mutateAsync(planToDelete.id);
        setDeleteDialogOpen(false);
        setPlanToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  // Determine if installment fields should be shown
  const showInstallmentFields = formData.type !== 'TERM';

  // Loading state
  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure payment plan options that families can choose from when setting up subscriptions.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payment plans. Please try again.
        </Alert>
      )}

      {/* Actions and Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                size="small"
              />
            }
            label="Hide inactive plans"
          />

          {filteredPlans.length !== paymentPlans.length && (
            <Chip
              label={`Showing ${filteredPlans.length} of ${paymentPlans.length} plans`}
              size="small"
              variant="outlined"
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setOptionsModalOpen(true)}
          >
            Payment Settings
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Add Payment Plan
          </Button>
        </Stack>
      </Stack>

      <DataTable
        columns={columns}
        data={filteredPlans}
        loading={isLoading}
        emptyMessage={
          paymentPlans.length > 0
            ? 'No payment plans match the current filter.'
            : 'No payment plans configured yet.'
        }
        searchPlaceholder="Search payment plans..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingPlan ? 'Edit Payment Plan' : 'Add Payment Plan'}
        submitLabel={editingPlan ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            placeholder="e.g., Monthly Standard"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />

          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value as PaymentPlanType })
              }
              label="Type"
            >
              {paymentPlanTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            placeholder="Describe this payment plan..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={2}
          />

          {showInstallmentFields && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Number of Installments"
                type="number"
                value={formData.installments}
                onChange={(e) => setFormData({ ...formData, installments: e.target.value })}
                fullWidth
                inputProps={{ min: 1, max: 52 }}
                helperText="Total number of payments"
              />

              <FormControl fullWidth>
                <InputLabel>Payment Interval</InputLabel>
                <Select
                  value={formData.interval}
                  onChange={(e) =>
                    setFormData({ ...formData, interval: e.target.value as PaymentInterval })
                  }
                  label="Payment Interval"
                >
                  {paymentIntervalOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="Discount"
              type="number"
              value={formData.discountPercent}
              onChange={(e) => setFormData({ ...formData, discountPercent: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              helperText="Discount for early/full payment"
            />

            <TextField
              label="Surcharge"
              type="number"
              value={formData.surchargePercent}
              onChange={(e) => setFormData({ ...formData, surchargePercent: e.target.value })}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 50, step: 0.5 }}
              helperText="Additional fee for installments"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            {editingPlan && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label={formData.isActive ? 'Active' : 'Inactive'}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Set as Default"
            />
          </Stack>
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Payment Plan"
        message={`Are you sure you want to delete "${planToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Payment Options Modal */}
      <PaymentOptionsModal
        open={optionsModalOpen}
        onClose={() => setOptionsModalOpen(false)}
      />
    </Box>
  );
}
